import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseQueryOptions, MutationOptions } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/store/authStore";
import { getERPClient } from "@/lib/erp/client";
import type { FrappeListParams } from "@/lib/frappe-client";
import { getQueryOptions } from "@/lib/config/queryConfig";

/**
 * Base hook for listing documents
 * Mode-aware: works in both direct (session) and control_plane (JWT) modes
 */
export function useListDocs<T = any>(
  doctype: string,
  filters?: Record<string, any>,
  options?: Omit<UseQueryOptions<T[], Error, T[], unknown[]>, "queryKey" | "queryFn">
) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<T[]>({
    queryKey: ["list", doctype, filters],
    queryFn: async () => {
      const client = getERPClient();
      const params: FrappeListParams = {
        doctype,
        filters,
      };
      return client.list<T>(params);
    },
    // @ts-expect-error - Overload mismatch may be intentional
    enabled: isAuthenticated, // Only run if authenticated
    ...getQueryOptions("list"),
    ...options,
  });
}

/**
 * Base hook for getting a single document
 * Mode-aware: works in both direct (session) and control_plane (JWT) modes
 */
export function useGetDoc<T = any>(
  doctype: string,
  name: string,
  options?: Omit<UseQueryOptions<T, Error, T, unknown[]>, "queryKey" | "queryFn">
) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<T>({
    queryKey: ["get", doctype, name],
    queryFn: async () => {
      const client = getERPClient();
      return client.get<T>({ doctype, name });
    },
    // @ts-expect-error - Overload mismatch may be intentional
    enabled: !!name && isAuthenticated, // Only run if name exists and authenticated
    ...getQueryOptions("detail"),
    ...options,
  });
}

/**
 * Base hook for creating a document
 * Mode-aware: works in both direct (session) and control_plane (JWT) modes
 */
export function useCreateDoc<T = any>(
  doctype: string,
  options?: Omit<MutationOptions<T, Error, Record<string, any>, unknown>, "mutationFn">
) {
  const queryClient = useQueryClient();

  return useMutation<T, Error, Record<string, any>>({
    mutationFn: async (data) => {
      const client = getERPClient();
      return client.create<T>({ doctype, data });
    },
    onSuccess: () => {
      // Invalidate list queries for this doctype
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
    },
    ...options,
  });
}

/**
 * Base hook for updating a document
 * Mode-aware: works in both direct (session) and control_plane (JWT) modes
 */
export function useUpdateDoc<T = any>(
  doctype: string,
  name: string,
  options?: Omit<MutationOptions<T, Error, Record<string, any>, unknown>, "mutationFn">
) {
  const queryClient = useQueryClient();

  return useMutation<T, Error, Record<string, any>>({
    mutationFn: async (data) => {
      const client = getERPClient();
      return client.update<T>({ doctype, name, data });
    },
    onSuccess: () => {
      // Invalidate both the specific doc and list queries
      queryClient.invalidateQueries({ queryKey: ["get", doctype, name] });
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
    },
    ...options,
  });
}

/**
 * Base hook for deleting a document
 * Mode-aware: works in both direct (session) and control_plane (JWT) modes
 */
export function useDeleteDoc(
  doctype: string,
  options?: Omit<MutationOptions<void, Error, string, unknown>, "mutationFn">
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (name) => {
      const client = getERPClient();
      return client.delete({ doctype, name });
    },
    onSuccess: () => {
      // Invalidate list queries for this doctype
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
    },
    ...options,
  });
}

